import * as React from 'react';
import PrimeReact, { PrimeReactContext } from 'primereact/api';
import { Calendar as PrimeCalendar, CalendarProps, CalendarSelectionMode } from 'primereact/calendar';

export type ViewportCalendarProps<TMode extends CalendarSelectionMode = 'single'> = CalendarProps<TMode> & {
  keepInViewport?: boolean;
  ref?: React.Ref<PrimeCalendar<TMode>>;
};

type CalendarElements = Pick<PrimeCalendar, 'getOverlay' | 'getInput'>;
type ViewportSession = { readonly active: boolean; refresh(): void; pause(): void; dispose(): void };
type OwnedStyle = { original: string; priority: string; written: string };

function watchViewport(calendar: CalendarElements): ViewportSession | null {
  const panel = calendar.getOverlay();
  const input = calendar.getInput();
  if (!panel || !input || panel.parentElement !== document.body) return null;

  const headers = Array.from(document.querySelectorAll<HTMLElement>('header.pda-app-header, header.app-header'));
  const originalComputed = getComputedStyle(panel);
  const originalMaxWidth = originalComputed.maxWidth;
  const originalMaxHeight = originalComputed.maxHeight;
  const ownedStyles = new Map<string, OwnedStyle>();
  let frame: number | null = null;
  let active = true;

  const originalStyle = (property: string): string => {
    const current = panel.style.getPropertyValue(property);
    const owned = ownedStyles.get(property);
    if (!owned) return current;
    if (current !== owned.written || panel.style.getPropertyPriority(property)) {
      owned.original = current;
      owned.priority = panel.style.getPropertyPriority(property);
    }
    return owned.original;
  };

  const writeStyle = (property: string, value: string): void => {
    const original = originalStyle(property);
    const owned = ownedStyles.get(property) ?? {
      original,
      priority: panel.style.getPropertyPriority(property),
      written: '',
    };
    if (panel.style.getPropertyValue(property) !== value || panel.style.getPropertyPriority(property)) {
      panel.style.setProperty(property, value);
    }
    owned.written = panel.style.getPropertyValue(property);
    ownedStyles.set(property, owned);
  };

  const limit = (original: string, available: number): string =>
    original && original !== 'none' ? `min(${original}, ${available}px)` : `${available}px`;

  const schedule = (): void => {
    if (!active || frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      refresh();
    });
  };
  // отложенное позиционирование библиотеки исправляется до следующего кадра
  const mutations = new MutationObserver(() => refresh());
  const sizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
  const visualViewport = window.visualViewport;

  const refresh = (): void => {
    if (!active || !panel.isConnected || !input.isConnected) return;
    // собственные изменения стилей не должны запускать новый цикл наблюдения
    mutations.disconnect();
    try {
      const viewportLeft = visualViewport?.offsetLeft ?? 0;
      const viewportTop = visualViewport?.offsetTop ?? 0;
      const viewportWidth = visualViewport?.width ?? document.documentElement.clientWidth;
      const viewportHeight = visualViewport?.height ?? document.documentElement.clientHeight;
      if (viewportWidth <= 0 || viewportHeight <= 0) return;

      const left = viewportLeft + Math.min(8, viewportWidth / 2);
      const right = viewportLeft + viewportWidth - Math.min(8, viewportWidth / 2);
      const bottom = viewportTop + viewportHeight - Math.min(8, viewportHeight / 2);
      let top = viewportTop + Math.min(8, viewportHeight / 2);
      for (const header of headers) {
        const rect = header.getBoundingClientRect();
        const visible = getComputedStyle(header).visibility;
        if (visible !== 'hidden' && visible !== 'collapse' && rect.width > 0 && rect.height > 0 &&
          rect.left < right && rect.right > left && rect.top < bottom && rect.bottom > viewportTop) {
          top = Math.max(top, rect.bottom + 8);
        }
      }
      top = Math.min(top, bottom - 1);
      const width = Math.max(1, right - left);
      const height = Math.max(1, bottom - top);

      writeStyle('max-width', limit(originalStyle('max-width') || originalMaxWidth, width));
      writeStyle('max-height', limit(originalStyle('max-height') || originalMaxHeight, height));
      writeStyle('min-width', limit(originalStyle('min-width') || '0px', width));
      writeStyle('min-height', '0px');
      writeStyle('box-sizing', 'border-box');
      writeStyle('overflow-x', 'auto');
      writeStyle('overflow-y', 'auto');

      const anchor = input.getBoundingClientRect();
      // размеры панели не должны зависеть от масштаба анимации раскрытия
      const panelWidth = panel.offsetWidth;
      const panelHeight = panel.offsetHeight;
      const above = anchor.bottom + panelHeight > bottom;
      const preferredTop = above ? anchor.top - panelHeight : anchor.bottom;
      const preferredLeft = anchor.left + panelWidth > right ? anchor.right - panelWidth : anchor.left;
      const fittedTop = Math.max(top, Math.min(preferredTop, bottom - panelHeight));
      const fittedLeft = Math.max(left, Math.min(preferredLeft, right - panelWidth));
      writeStyle('top', `${fittedTop + window.scrollY}px`);
      writeStyle('left', `${fittedLeft + window.scrollX}px`);
      writeStyle('transform-origin', above ? 'bottom' : 'top');
    } finally {
      if (active) mutations.observe(panel, { attributes: true, attributeFilter: ['style'] });
    }
  };

  const onScroll = (event: Event): void => {
    if (event.target instanceof Node && panel.contains(event.target)) return;
    schedule();
  };

  const pause = (): void => {
    if (!active) return;
    active = false;
    mutations.disconnect();
    sizes?.disconnect();
    if (frame !== null) window.cancelAnimationFrame(frame);
    frame = null;
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    visualViewport?.removeEventListener('scroll', schedule);
    visualViewport?.removeEventListener('resize', schedule);
  };

  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  visualViewport?.addEventListener('scroll', schedule);
  visualViewport?.addEventListener('resize', schedule);
  sizes?.observe(panel);
  sizes?.observe(input);
  headers.forEach(header => sizes?.observe(header));
  refresh();

  return {
    get active() { return active; },
    refresh,
    pause,
    dispose: () => {
      pause();
      for (const [property, owned] of ownedStyles) {
        if (panel.style.getPropertyValue(property) === owned.written && !panel.style.getPropertyPriority(property)) {
          if (owned.original) panel.style.setProperty(property, owned.original, owned.priority);
          else panel.style.removeProperty(property);
        }
      }
    },
  };
}

export function ViewportCalendar<TMode extends CalendarSelectionMode = 'single'>({
  keepInViewport,
  ...props
}: ViewportCalendarProps<TMode>) {
  const context = React.useContext(PrimeReactContext);
  const calendarRef = React.useRef<PrimeCalendar<TMode>>(null);
  const sessionRef = React.useRef<ViewportSession | null>(null);
  const pendingStartRef = React.useRef(false);
  const appendTo = props.appendTo || context?.appendTo || PrimeReact.appendTo;
  const enabled = keepInViewport === true && !props.inline && !props.touchUI &&
    typeof document !== 'undefined' && (!appendTo || appendTo === document.body);

  const dispose = React.useCallback(() => {
    pendingStartRef.current = false;
    sessionRef.current?.dispose();
    sessionRef.current = null;
  }, []);

  const start = React.useCallback(() => {
    if (sessionRef.current && !sessionRef.current.active) dispose();
    if (!sessionRef.current && calendarRef.current) {
      sessionRef.current = watchViewport(calendarRef.current);
    }
    if (sessionRef.current) pendingStartRef.current = false;
    sessionRef.current?.refresh();
  }, [dispose]);

  const callerRef = props.ref;
  const mergedRef = React.useCallback((instance: PrimeCalendar<TMode> | null) => {
    calendarRef.current = instance;
    let cleanup: (() => void) | void = undefined;
    if (typeof callerRef === 'function') {
      cleanup = callerRef(instance);
    } else if (callerRef) {
      callerRef.current = instance;
    }
    // библиотека восстанавливает imperative ref после дочернего onEnter
    if (instance && pendingStartRef.current) start();
    if (typeof cleanup === 'function') return () => {
      calendarRef.current = null;
      cleanup();
    };
  }, [callerRef, start]);

  React.useLayoutEffect(() => {
    if (enabled) start();
    return dispose;
  }, [enabled, start, dispose]);

  const transitionOptions: NonNullable<CalendarProps['transitionOptions']> = {
    timeout: { enter: 120, exit: 100 },
    ...props.transitionOptions,
    onEnter: (...args) => {
      props.transitionOptions?.onEnter?.(...args);
      dispose();
      pendingStartRef.current = true;
      start();
    },
    onEntered: (...args) => {
      props.transitionOptions?.onEntered?.(...args);
      start();
    },
    onExit: (...args) => {
      pendingStartRef.current = false;
      sessionRef.current?.pause();
      props.transitionOptions?.onExit?.(...args);
    },
    onExited: (...args) => {
      dispose();
      props.transitionOptions?.onExited?.(...args);
    },
  };

  return <PrimeCalendar<TMode> {...props} {...(enabled ? { ref: mergedRef, transitionOptions } : {})} />;
}
